Object.defineProperty(Array.prototype, "getPreview", {
  value: function () {
    if (this.length > 0) {
      const firstItem = this[0];
      if (firstItem instanceof Blob || firstItem instanceof File) {
        const blobUrl = URL.createObjectURL(firstItem);
        return blobUrl;
      }
    }
    return null; // Return null if the array is empty or the first item is not a Blob or File
  },
  enumerable: false,
});

export function hasHistory() {
  return window.history.length > 2;
}

/**
 * Terug naar `path`. Komen we daar ook echt vandaan, dan via de history, zodat
 * de query meekomt — die draagt de filters van een overzicht (zie
 * mixins/routeFilters.js). Een `push` zou ze stilletjes weggooien.
 *
 * vue-router bewaart het vorige adres in `history.state.back`; staat daar iets
 * anders (of niets, bij een gedeelde link), dan is een push het enige wat kan.
 */
export function backToPath(router, path) {
  const previous = window.history.state && window.history.state.back;

  if (typeof previous === "string" && previous.split("?")[0].split("#")[0] === path) {
    router.back();
    return;
  }

  router.push(path);
}

export function normalizeToArray(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === "object") {
    return Object.entries(value).map(([key, item]) => {
      const normalizedItem =
        item && typeof item === "object" ? item : { value: item };

      return {
        ...normalizedItem,
        id: normalizedItem?.id ?? key,
      };
    });
  }

  return [];
}

export function getStatusColor(status) {
  switch (status) {
    case "in_progress":
      return "blue";
    case "completed":
      return "green";
    case "on_hold":
      return "orange";
    case "canceled":
      return "red";
    case "edit_request":
      return "purple";
    case "not_started":
      return "grey";
  }
}

export function getStatusText(status) {
  switch (status) {
    case "not_started":
      return "Niet gestart";
    case "in_progress":
      return "In bewerking";
    case "completed":
      return "Voltooid";
    case "on_hold":
      return "In afwachting";
    case "canceled":
      return "Geannuleerd";
    case "edit_request":
      return "Wijzigingsverzoek";
    default:
      return "";
  }
}

export function convertUnixToDate(unix) {
  if (unix === null || unix === undefined || unix === "") return "";

  const n = Number(unix);
  if (!Number.isFinite(n)) return "";

  const ms = n < 1e12 ? n * 1000 : n; // supports seconds or milliseconds
  const d = new Date(ms);

  if (Number.isNaN(d.getTime())) return "";

  const dd = String(d.getDate()).padStart(2, "0");
  const dutchMonths = [
    "jan.",
    "feb.",
    "mrt.",
    "april",
    "mei",
    "jun.",
    "jul.",
    "aug.",
    "sep.",
    "okt.",
    "nov.",
    "dec.",
  ];
  const month = dutchMonths[d.getMonth()];
  const yyyy = String(d.getFullYear());

  return `${dd} ${month} ${yyyy}`;
}

export function formatDate(value) {
  if (!value) return value;

  // Intake answers are date-only values in either Dutch (DD-MM-YYYY) or ISO
  // (YYYY-MM-DD) format. Passing either directly to `new Date(string)` makes
  // their interpretation browser- and timezone-dependent.
  if (
    typeof value === "string" &&
    /^(?:\d{2}-\d{2}-\d{4}|\d{4}-\d{2}-\d{2})$/.test(value.trim())
  ) {
    return formatIntakeDateAnswer(value);
  }

  const d = new Date(value);
  if (isNaN(d.getTime())) return value;

  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getUTCFullYear());

  return `${dd}-${mm}-${yyyy}`;
}

function createValidLocalDate(year, month, day) {
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

export function parseIntakeDateAnswer(value) {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;

    return createValidLocalDate(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate(),
    );
  }

  if (typeof value !== "string") return null;

  const trimmedValue = value.trim();
  const dayMonthYearMatch = trimmedValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dayMonthYearMatch) {
    return createValidLocalDate(
      Number(dayMonthYearMatch[3]),
      Number(dayMonthYearMatch[2]),
      Number(dayMonthYearMatch[1]),
    );
  }

  const isoDateMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateMatch) {
    return createValidLocalDate(
      Number(isoDateMatch[1]),
      Number(isoDateMatch[2]),
      Number(isoDateMatch[3]),
    );
  }

  return null;
}

export function formatIntakeDateAnswer(value) {
  if (value === null || value === undefined || value === "") return null;

  const date = parseIntakeDateAnswer(value);
  if (!date) {
    return typeof value === "string" ? value : null;
  }

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());

  return `${dd}-${mm}-${yyyy}`;
}

export function convertUnixtoDateAndTime(unix) {
  if (unix === null || unix === undefined || unix === "") return "";

  const n = Number(unix);
  if (!Number.isFinite(n)) return "";

  const ms = n < 1e12 ? n * 1000 : n; // supports seconds or milliseconds
  const d = new Date(ms);

  if (Number.isNaN(d.getTime())) return "";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
}
export function convertUnixtoTime(unix) {
  if (unix === null || unix === undefined || unix === "") return "";

  const n = Number(unix);
  if (!Number.isFinite(n)) return "";

  const ms = n < 1e12 ? n * 1000 : n; // supports seconds or milliseconds
  const d = new Date(ms);

  if (Number.isNaN(d.getTime())) return "";

  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  return `${hh}:${min}`;
}
export function convertUnixtoDateAndTimeObject(unix) {
  if (unix === null || unix === undefined || unix === "") return "";

  const n = Number(unix);
  if (!Number.isFinite(n)) return "";

  const ms = n < 1e12 ? n * 1000 : n; // supports seconds or milliseconds
  const d = new Date(ms);

  if (Number.isNaN(d.getTime())) return "";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  return {
    year: `${dd}-${mm}-${yyyy}`,
    time: `${hh}:${min}`,
  };
}

export function getEditRequestImpactText(impact) {
  switch (impact) {
    case "offerte_impact":
    case "offerte_impact_hoger":
    case "offerte_impact_lager":
      return "Offerteimpact";
    case "wijzigingsverzoek":
      return "Planningimpact";
    default:
      return "";
  }
}

export function isMoreThanCutoffHoursBefore(unixDateTs, timeHHMM, cutoffHours) {
  const ms = unixDateTs > 1e12 ? unixDateTs : unixDateTs * 1000;

  const [hh, mm] = String(timeHHMM || "00:00").split(":");
  const h = Number(hh);
  const m = Number(mm);

  const target = new Date(ms);
  target.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0); // local time

  const diffHours = (target.getTime() - Date.now()) / 36e5;

  // true = too late (within cutoff window OR already past)
  return diffHours <= cutoffHours;
}
